import {hasKey, Random} from 'yougile-util'

export interface CollectionChange<T> {
  add?: T | T[]
  remove?: T | T[]
  set?: T | T[]
  intersect?: T | T[]
  clear?: boolean
}

export type CollectionValue<T> =
  | T
  | {union: CollectionValue<T>[]}
  | {intersect: CollectionValue<T>[]}
  | {random: CollectionValue<T>; items?: number}
  | {exclude: CollectionValue<T>; from: CollectionValue<T>}

export const mapCollection = <T, R>(
  collection: CollectionValue<T>,
  fn: (v: T) => R[],
  recur = 0
): R[] => {
  if (recur > 10) {
    return []
  }
  if (hasKey(collection, 'union')) {
    const res = new Set<R>()
    for (const c of collection.union) {
      for (const r of mapCollection(c, fn, recur + 1)) {
        res.add(r)
      }
    }
    return [...res]
  }
  if (hasKey(collection, 'intersect')) {
    let res: Set<R> | undefined
    for (const c of collection.intersect) {
      const curr = new Set<R>()
      for (const r of mapCollection(c, fn, recur + 1)) {
        curr.add(r)
      }
      if (!res) {
        res = curr
      } else {
        for (const item of res.values()) {
          if (!curr.has(item)) {
            res.delete(item)
          }
        }
      }
    }
    return [...(res ?? new Set<R>())]
  }
  if (hasKey(collection, 'exclude')) {
    if (!hasKey(collection, 'from')) {
      return []
    }
    const exclude = new Set(mapCollection(collection.exclude, fn, recur + 1))
    return mapCollection(collection.from, fn, recur + 1).filter(
      r => !exclude.has(r)
    )
  }
  if (hasKey(collection, 'random')) {
    const arr = [...mapCollection(collection.random, fn, recur + 1)]
    const count = collection.items ?? 1
    return Random.pickItems(arr, count)
  }
  return fn(collection)
}

export const isCollection = <T>(
  obj: unknown,
  isValue: (v: unknown) => v is T,
  recur = 0
): obj is CollectionValue<T> => {
  if (recur > 3) {
    return false
  }
  return (
    isValue(obj) ||
    (hasKey(obj, 'union') &&
      obj.union instanceof Array &&
      !obj.union.some(v => !isCollection(v, isValue, recur + 1))) ||
    (hasKey(obj, 'intersect') &&
      obj.intersect instanceof Array &&
      !obj.intersect.some(v => !isCollection(v, isValue, recur + 1))) ||
    (hasKey(obj, 'random') &&
      obj.random instanceof Array &&
      !obj.random.some(v => !isCollection(v, isValue, recur + 1)) &&
      (!hasKey(obj, 'items') || Number.isInteger(obj.items))) ||
    (hasKey(obj, 'exclude') &&
      hasKey(obj, 'from') &&
      isCollection(obj.exclude, isValue, recur + 1) &&
      isCollection(obj.from, isValue, recur + 1))
  )
}

export type ValueChange<T> = T extends (infer U)[]
  ? U | T | CollectionChange<U>
  : T
